import { DEFAULT_CHART_COLORS } from '@utilities';

import { addSlideFooter } from '../slide_footer';
import { parseRichTextToPptRuns } from '../helpers.js';
import { FOOTER_TEXT, LINE_COLOR, SURVEY_TITLE_OPTIONS, TEXT_COLOR } from '../constants';

const MARKER_COLOR = '000000';
const MAX_RENDERED_CURVE_POINTS = 9;
const PRICE_POINT_LABEL_CURVE_PAIRS = {
  IPP: ['getting expensive', 'good value'],
  OPP: ['too cheap', 'too expensive'],
  PMC: ['too cheap', 'getting expensive'],
  PME: ['good value', 'too expensive'],
};

const toNumericPrice = (category) => Number(String(category).replace(/[^0-9.-]/g, ''));

const lerp = (a, b, t) => a + (b - a) * t;

const getCleanAxisBounds = (min, max) => {
  const range = max - min || 1;
  const step = 10 ** Math.floor(Math.log10(range));
  return { max: Math.ceil(max / step) * step, min: Math.floor(min / step) * step };
};

const getXAxisTitle = (format) => {
  if (!format) return 'Value';
  if (format.includes('%')) return 'Percent (%)';
  if (format.includes('$')) return 'Currency ($)';
  return 'Value';
};

const findSeriesByName = (series, name) =>
  series.find((candidate) => candidate.name?.trim().toLowerCase() === name);

/** Reads the price points (PMC/OPP/IPP/PME) and their prices straight from the segment's table. */
const getPricePointsFromTable = (table) => {
  const row = table?.data?.[0];
  if (!row) return [];

  return table.columns
    .filter(({ accessor }) => row[accessor] != null)
    .map(({ accessor }) => ({ label: accessor.toUpperCase(), price: Number(row[accessor]) }))
    .filter(({ price }) => Number.isFinite(price));
};

/** Estimates a curve's value at a price that falls between two of its sampled points. */
const interpolateAt = (priceValues, values, price) => {
  for (let i = 0; i < priceValues.length - 1; i += 1) {
    if (price >= priceValues[i] && price <= priceValues[i + 1]) {
      const t = (price - priceValues[i]) / (priceValues[i + 1] - priceValues[i]);
      return lerp(values[i], values[i + 1], t);
    }
  }
  return values[values.length - 1];
};

/** Averages two curves' values at a given price */
const averagePairValueAt = (priceValues, seriesA, seriesB, price) =>
  (interpolateAt(priceValues, seriesA.values, price) +
    interpolateAt(priceValues, seriesB.values, price)) /
  2;

/** Works out the y-value for a price point's dot by averaging the two curves it sits between */
const valueAtPricePointFor = (label, priceValues, curveSeries, price) => {
  const pairNames = PRICE_POINT_LABEL_CURVE_PAIRS[label];
  const seriesA = pairNames && findSeriesByName(curveSeries, pairNames[0]);
  const seriesB = pairNames && findSeriesByName(curveSeries, pairNames[1]);
  if (seriesA && seriesB) return averagePairValueAt(priceValues, seriesA, seriesB, price);

  const valuesAtPrice = curveSeries.map((series) =>
    interpolateAt(priceValues, series.values, price)
  );
  let closestPair = [valuesAtPrice[0], valuesAtPrice[1]];
  let smallestDiff = Infinity;
  for (let i = 0; i < valuesAtPrice.length; i += 1) {
    for (let j = i + 1; j < valuesAtPrice.length; j += 1) {
      const diff = Math.abs(valuesAtPrice[i] - valuesAtPrice[j]);
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestPair = [valuesAtPrice[i], valuesAtPrice[j]];
      }
    }
  }
  return (closestPair[0] + closestPair[1]) / 2;
};

/** Thins out the price axis */
const buildRenderGrid = (priceValues) => {
  if (priceValues.length <= MAX_RENDERED_CURVE_POINTS) return priceValues;

  const min = priceValues[0];
  const max = priceValues[priceValues.length - 1];
  const step = (max - min) / (MAX_RENDERED_CURVE_POINTS - 1);
  return Array.from({ length: MAX_RENDERED_CURVE_POINTS }, (_, index) => min + index * step);
};

/** Builds the chart's series. */
const buildVanWestendorpChartData = (chart, table) => {
  if (!chart || !Array.isArray(chart.series) || !chart.series.length) return null;

  const priceValues = (chart.xAxis?.categories || []).map(toNumericPrice);
  if (!priceValues.length || priceValues.some((value) => !Number.isFinite(value))) return null;

  const curveSeries = chart.series.map((series) => ({
    name: series.name,
    values: series.data.map((value) => Number(value)),
  }));

  const pricePoints = getPricePointsFromTable(table);
  const renderGrid = buildRenderGrid(priceValues);
  const mergedPrices = Array.from(
    new Set([...renderGrid, ...pricePoints.map(({ price }) => price)])
  ).sort((a, b) => a - b);

  const mergedCurveSeries = curveSeries.map((series) => ({
    name: series.name,
    values: mergedPrices.map((price) => interpolateAt(priceValues, series.values, price)),
  }));

  const markerSeries = pricePoints.map(({ label, price }) => ({
    name: label,
    values: mergedPrices.map((candidate) =>
      candidate === price ? valueAtPricePointFor(label, priceValues, curveSeries, price) : null
    ),
  }));

  return {
    curveCount: curveSeries.length,
    dataSeries: [
      { name: chart.xAxis?.title || 'Price', values: mergedPrices },
      ...mergedCurveSeries,
      ...markerSeries,
    ],
    markerCount: markerSeries.length,
  };
};

/** Creates the demand-curve chart slide, with the price points plotted as dots. */
export const createVanWestendorpChartSlide = ({
  chart,
  pptx,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  slideSuffix = '',
  table,
  title,
}) => {
  const chartData = buildVanWestendorpChartData(chart, table);
  if (!chartData) return null;

  const { curveCount, dataSeries, markerCount } = chartData;
  const priceValues = dataSeries[0].values;
  const { max: priceMax, min: priceMin } = getCleanAxisBounds(
    Math.min(...priceValues),
    Math.max(...priceValues)
  );

  const isPercent = chart.format?.includes('%') ?? true;
  const valueMax = isPercent
    ? 1
    : chart.series.reduce((max, series) => Math.max(max, ...(series.data ?? [])), 0);

  const questionTitleRuns = parseRichTextToPptRuns(questionTitle);
  if (!questionTitleRuns.length) return null;

  const slide = pptx.addSlide();

  const titleRuns = [
    ...(questionNumber ? [{ text: `Q${questionNumber}. ` }] : []),
    ...questionTitleRuns,
    ...(slideSuffix ? [{ text: slideSuffix }] : []),
  ];
  slide.addText(titleRuns, SURVEY_TITLE_OPTIONS);

  slide.addChart(pptx.charts.SCATTER, dataSeries, {
    catAxisLabelFontSize: 8,
    catAxisMaxVal: priceMax,
    catAxisMinVal: priceMin,
    catAxisTitle: chart.xAxis?.title || getXAxisTitle(table?.columns?.[0]?.format),
    catGridLine: { color: LINE_COLOR, size: 0.5, style: 'solid' },
    chartColors: [
      ...DEFAULT_CHART_COLORS.slice(0, curveCount),
      ...Array(markerCount).fill(MARKER_COLOR),
    ],
    h: '62%',
    legendColor: TEXT_COLOR,
    legendFontSize: 10,
    legendPos: 't',
    lineDataSymbolSize: 5,
    showCatAxisTitle: true,
    showLegend: dataSeries.length > 2,
    showValAxisTitle: true,
    valAxisLabelFontSize: 8,
    valAxisLabelFormatCode: chart.format,
    valAxisLineShow: false,
    valAxisMaxVal: valueMax,
    valAxisMinVal: 0,
    valAxisTitle: isPercent ? 'Cumulative %' : 'Cumulative Count',
    w: '85%',
    x: 0.75,
    y: 1.1,
  });

  addSlideFooter({ pptx, reportType, reportUrl, slide });

  slide.addText(
    [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
    FOOTER_TEXT
  );

  return slide;
};
